import React, { useState, useEffect } from "react";
import Spinner from "../loading/spinner";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

const getAppointments = () => {
  try {
    return JSON.parse(localStorage.getItem("appointments")) || [];
  } catch {
    return [];
  }
};

const saveAppointments = (appointments) => {
  localStorage.setItem("appointments", JSON.stringify(appointments));
};

const getClinics = () => {
  try {
    return JSON.parse(localStorage.getItem("clinics")) || [];
  } catch {
    return [];
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const PaymentMode = ({ patientDetails, reset }) => {
  const [loading, setLoading] = useState(false);
  const [clinicFees, setClinicFees] = useState({
    clinicNewFees: null,
    clinicOldFees: null,
    clinicId: null,
  });

  // Load clinic fees based on patient's clinic
  useEffect(() => {
    const clinics = getClinics();
    // Try to find clinic by id or location
    let clinic = null;
    if (patientDetails.clinicId) {
      clinic = clinics.find((c) => c.id === patientDetails.clinicId);
    }
    if (!clinic && patientDetails.clinicLocation) {
      clinic = clinics.find((c) => c.location === patientDetails.clinicLocation);
    }
    if (clinic) {
      setClinicFees({
        clinicNewFees: clinic.clinicNewFees || 0,
        clinicOldFees: clinic.clinicOldFees || 0,
        clinicId: clinic.id,
      });
    }
  }, [patientDetails]);

  const formik = useFormik({
    initialValues: {
      customFees: "",
      fees: "",
      modePayment: "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      fees: Yup.string().required("Fees is required"),
      modePayment: Yup.string().required("Mode of Payment is required"),
    }),
    onSubmit: (values) => {
      handlePayment(values);
    },
  });

  const handlePayment = (values) => {
    if (!patientDetails || !patientDetails.id) {
      toast.error("Invalid appointment data.");
      return;
    }

    setLoading(true);

    // Get all appointments
    const appointments = getAppointments();

    // Find the appointment
    const index = appointments.findIndex((apt) => apt.id === patientDetails.id);
    if (index === -1) {
      toast.error("Appointment not found.");
      setLoading(false);
      return;
    }

    // Update the appointment
    const appointment = appointments[index];
    const updatedAppointment = {
      ...appointment,
      paymentStatus: "PAID",
      appointmentvisitStatus: "VISITED",
      paymentMode: values.modePayment,
      paymentAmount: parseFloat(values.fees),
      paymentDate: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // If appointment is already visited, still update payment
    appointments[index] = updatedAppointment;
    saveAppointments(appointments);

    toast.success("Payment successful! Appointment marked as visited.");
    setLoading(false);

    // Close modal and refresh parent
    reset();
  };

  return (
    <>
      <div
        id="login-popup"
        tabIndex="-1"
        className="bg-black/50 overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 h-full items-center justify-center flex"
      >
        <div className="relative p-4 w-full max-w-xl h-full md:h-auto">
          <div className="relative bg-white rounded-lg shadow">
            <button
              type="button"
              className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center popup-close"
              onClick={reset}
            >
              <svg
                aria-hidden="true"
                className="w-5 h-5"
                fill="#c6c7c7"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close popup</span>
            </button>
            <div className="p-5">
              <div className="text-center">
                <p className="mb-3 text-2xl font-semibold leading-5 text-slate-900">
                  Select Payment Option
                </p>
              </div>
              {/* Form */}
              <form onSubmit={formik.handleSubmit}>
                <div className="">
                  <div className="flex flex-wrap lg:w-4/5 sm:mx-auto sm:mb-2 -mx-2">
                    <div className="p-2 w-full">
                      <div className="bg-gray-100 rounded flex p-4 h-full items-center">
                        <input
                          type="radio"
                          name="fees"
                          value={
                            clinicFees.clinicNewFees !== null
                              ? String(clinicFees.clinicNewFees)
                              : ""
                          }
                          onChange={formik.handleChange}
                          checked={
                            formik.values.fees ===
                            (clinicFees.clinicNewFees !== null
                              ? String(clinicFees.clinicNewFees)
                              : "")
                          }
                          className="mr-6 form-radio h-5 w-5 text-blue-600"
                        />
                        <span className="title-font font-medium">
                          New Patient Fees ₹{clinicFees.clinicNewFees || 0}
                        </span>
                      </div>
                    </div>
                    <div className="p-2 w-full">
                      <div className="bg-gray-100 rounded flex p-4 h-full items-center">
                        <input
                          type="radio"
                          name="fees"
                          value={
                            clinicFees.clinicOldFees !== null
                              ? String(clinicFees.clinicOldFees)
                              : ""
                          }
                          onChange={formik.handleChange}
                          checked={
                            formik.values.fees ===
                            (clinicFees.clinicOldFees !== null
                              ? String(clinicFees.clinicOldFees)
                              : "")
                          }
                          className="mr-6 form-radio h-5 w-5 text-blue-600"
                        />
                        <span className="title-font font-medium">
                          Old Patient Fees ₹{clinicFees.clinicOldFees || 0}
                        </span>
                      </div>
                    </div>
                    <div className="p-2 w-full">
                      <div className="bg-gray-100 rounded flex p-4 h-full items-center">
                        <input
                          type="radio"
                          name="fees"
                          value="custom"
                          onChange={(e) => {
                            formik.setFieldValue(
                              "fees",
                              formik.values.customFees || ""
                            );
                          }}
                          checked={
                            formik.values.customFees !== "" &&
                            formik.values.fees === formik.values.customFees
                          }
                          className="mr-6 form-radio h-5 w-5 text-blue-600"
                        />
                        <span className="title-font font-medium">
                          Custom Fees:
                          <input
                            name="customFees"
                            onChange={formik.handleChange}
                            value={formik.values.customFees}
                            className="border-b-2 w-20 border-blue-400 focus:border-blue-500 focus:outline-none ml-2"
                            type="text"
                            placeholder="Amount"
                          />
                          <span className="text-red-800 text-[11px]">
                            * please write amount first
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 mx-auto">
                    <div className="flex flex-wrap -m-2">
                      <div className="p-2 w-1/2">
                        <div className="relative">
                          <button
                            type="submit"
                            name="modePayment"
                            value="upi"
                            onClick={() =>
                              formik.setFieldValue("modePayment", "upi")
                            }
                            className={`w-full rounded-3xl border border-gray-300 focus:ring-2 text-base outline-none text-white py-1 px-16 leading-8 transition-colors duration-200 ease-in-out ${
                              formik.values.modePayment === "upi"
                                ? "bg-red-600 hover:bg-red-500"
                                : "bg-gray-400 hover:bg-gray-500"
                            }`}
                          >
                            UPI
                          </button>
                        </div>
                      </div>
                      <div className="p-2 w-1/2">
                        <div className="relative">
                          <button
                            type="submit"
                            name="modePayment"
                            value="cash"
                            onClick={() =>
                              formik.setFieldValue("modePayment", "cash")
                            }
                            className={`w-full rounded-3xl border border-gray-300 focus:ring-2 text-base outline-none text-white py-1 px-16 leading-8 transition-colors duration-200 ease-in-out ${
                              formik.values.modePayment === "cash"
                                ? "bg-red-600 hover:bg-red-500"
                                : "bg-gray-400 hover:bg-gray-500"
                            }`}
                          >
                            CASH
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {loading && <Spinner />}
      <ToastContainer />
    </>
  );
};

export default PaymentMode;